// Parity P1 wave 1: the task header (hybrid design §3.1 "breadcrumb links, star, Follow bell
// (fixed, plus a watchers popover listing followers), ⧉ copy link, ↗ full page (⇧F), ⋯
// (Duplicate, Customize layout (managers), Copy ID, Move to trash)"; B4-ITEM-TEMPLATES adds
// "Save as template…"). The bell reads the task's
// watchers (`tasks.watchers`): an assignee or the creator follows without having pressed it
// (the old bell started empty and never loaded: design §3 "Bug").
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
  AvatarTile,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  toast,
} from "@companyos/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import {
  BellIcon,
  BellRingIcon,
  BookmarkPlusIcon,
  CopyIcon,
  EllipsisIcon,
  HashIcon,
  KeyboardIcon,
  LayoutTemplateIcon,
  LinkIcon,
  Maximize2Icon,
  RepeatIcon,
  Trash2Icon,
} from "lucide-react";
import { useState } from "react";
import type { Api } from "../../../api/client";
import { keepFocus } from "../../../focus/focus";
import { RepeatDialog } from "../../automations/routines";
import { RaiseIssueButton } from "../../cadence/issue-dialogs";
import { type CompanyAccess, isManager } from "../../company";
import { NotifyLevelMenu } from "../../notifications/notify-level-menu";
import { DuplicateTaskDialog } from "../duplicate-dialogs";
import { SaveAsTemplateDialog } from "../item-templates";
import { taskRef } from "../model";
import { useTaskWrites } from "../task-writes";
import type { Board, TaskDetail } from "../view-engine";
import { FavoriteStar } from "../work-screens";
import { KEYBOARD_MAP } from "./keyboard";
import { p1Keys, type TaskWatchers, watchersQuery } from "./queries";
import { StopAgentButton } from "./stop-agent";

const REASON: Record<TaskWatchers["data"][number]["reasons"][number], string> = {
  follow: "follows",
  assignee: "assignee",
  creator: "created it",
};

/** The breadcrumb: space › board · #ref, the first two links. */
export function TaskLocation({
  company,
  task,
  board,
  spaceName,
}: {
  company: CompanyAccess;
  task: TaskDetail;
  board: Board | undefined;
  spaceName: string | undefined;
}) {
  return (
    <span data-slot="task-location" className="flex min-w-0 items-center gap-1">
      {board && spaceName ? (
        <>
          <Link
            to="/c/$slug/work/s/$spaceId"
            params={{ slug: company.slug, spaceId: board.space_id }}
            search={keepFocus}
            className="truncate hover:text-foreground hover:underline"
          >
            {spaceName}
          </Link>
          <span aria-hidden="true">›</span>
        </>
      ) : null}
      {board ? (
        <Link
          to="/c/$slug/work/b/$boardId"
          params={{ slug: company.slug, boardId: board.id }}
          search={keepFocus}
          className="truncate hover:text-foreground hover:underline"
        >
          {board.name}
        </Link>
      ) : (
        <span>Board</span>
      )}
      <span aria-hidden="true">·</span>
      <span className="tabular-nums">{taskRef(task)}</span>
    </span>
  );
}

export function TaskHeaderActions({
  api,
  company,
  task,
  canManage,
  onClose,
  onOpenTask,
  onFullPage,
  onCustomize,
}: {
  api: Api;
  company: CompanyAccess;
  task: TaskDetail;
  /** Manages the board: "Customize layout" shows. */
  canManage: boolean;
  onClose: () => void;
  onOpenTask?: (id: string) => void;
  /** Absent on the full page itself. */
  onFullPage?: () => void;
  onCustomize: () => void;
}) {
  const co = company.id;
  const qc = useQueryClient();
  const [duplicating, setDuplicating] = useState(false);
  const [repeating, setRepeating] = useState(false);
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [keys, setKeys] = useState(false);
  const watchers = useQuery(watchersQuery(api, co, task.id));
  const following = watchers.data?.me.following ?? false;
  const follow = useMutation({
    mutationFn: (on: boolean) =>
      on
        ? api.client.follows.put({
            params: { co },
            body: { subject_kind: "task", subject_id: task.id },
          })
        : api.client.follows.delete({
            params: { co },
            query: { subject_kind: "task", subject_id: task.id },
          }),
    onMutate: (on) => {
      const before = qc.getQueryData<TaskWatchers>(p1Keys.watchers(co, task.id));
      if (before)
        qc.setQueryData<TaskWatchers>(p1Keys.watchers(co, task.id), {
          ...before,
          me: { following: on, explicit: on },
        });
      return { before };
    },
    onError: (_e, _on, ctx) => {
      if (ctx?.before) qc.setQueryData(p1Keys.watchers(co, task.id), ctx.before);
    },
    onSuccess: (_r, on) =>
      toast.add({ title: on ? "Following: changes land in your inbox" : "Stopped following" }),
    onSettled: () => void qc.invalidateQueries({ queryKey: p1Keys.watchers(co, task.id) }),
  });
  const writes = useTaskWrites(api, company);
  const remove = () => {
    writes.remove(task.id, {
      onSuccess: () => toast.add({ title: `${taskRef(task)} deleted`, description: task.title }),
    });
    onClose();
  };
  const copy = (text: string, what: string) =>
    void navigator.clipboard?.writeText(text).then(
      () => toast.add({ title: `${what} copied` }),
      () => toast.add({ title: `The ${what.toLowerCase()} could not be copied`, type: "warning" }),
    );
  const link = () => {
    const url = new URL(window.location.href);
    url.searchParams.set("task", task.id);
    return url.toString();
  };
  const list = watchers.data?.data ?? [];
  // An assignee follows through the assignment; unfollowing then means an explicit "off" is
  // not possible: the bell says why instead.
  const implicitOnly = following && !watchers.data?.me.explicit;
  return (
    <>
      {/* MEETINGS: raise the task as an issue on a team's list (a visible action). */}
      <RaiseIssueButton
        api={api}
        co={co}
        size="icon-sm"
        defaults={{
          title: task.title,
          source: { kind: "task", id: task.id, label: `Task ${taskRef(task)}` },
        }}
      />
      <FavoriteStar company={company} favorite={{ kind: "task", id: task.id }} name={task.title} />
      <span className="flex items-center">
        <Tooltip>
          <TooltipTrigger
            render={
              <Button
                variant="ghost"
                size="sm"
                className="gap-1 px-2"
                data-slot="follow-bell"
                aria-label={
                  following
                    ? implicitOnly
                      ? "Following as assignee"
                      : "Following: stop following"
                    : "Follow (get inbox updates)"
                }
                aria-pressed={following}
                disabled={watchers.isPending || follow.isPending || implicitOnly}
                onClick={() => follow.mutate(!following)}
              />
            }
          >
            {following ? <BellRingIcon aria-hidden="true" /> : <BellIcon aria-hidden="true" />}
            <span className="max-[1100px]:sr-only">{following ? "Following" : "Follow"}</span>
          </TooltipTrigger>
          <TooltipContent>
            {implicitOnly
              ? "You follow it as its assignee"
              : following
                ? "Stop following"
                : "Follow: changes land in your inbox"}
          </TooltipContent>
        </Tooltip>
        <Popover>
          <PopoverTrigger
            render={
              <Button
                variant="ghost"
                size="xs"
                className="tabular-nums"
                aria-label={`Watchers: ${list.length}`}
                data-slot="watchers-trigger"
              />
            }
          >
            {list.length}
          </PopoverTrigger>
          <PopoverContent className="w-72" align="end">
            <p className="mb-2 text-sm font-medium">Watching this task</p>
            {list.length ? (
              <ul aria-label="Watchers" className="m-0 flex list-none flex-col gap-1.5 p-0">
                {list.map((w) => (
                  <li key={w.membership_id} className="flex items-center gap-2 text-sm">
                    <AvatarTile name={w.name} kind={w.kind} size="sm" />
                    <span className="min-w-0 flex-1 truncate">{w.name}</span>
                    <span className="text-xs text-muted-foreground">
                      {w.reasons.map((r) => REASON[r]).join(" · ")}
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-muted-foreground">Nobody yet.</p>
            )}
          </PopoverContent>
        </Popover>
      </span>
      {/* INBOX-PREFS: what of this task reaches the person (All / Mentions / Mute). */}
      <NotifyLevelMenu api={api} co={co} subjectKind="task" subjectId={task.id} compact />
      <Tooltip>
        <TooltipTrigger
          render={
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label="Copy link"
              onClick={() => copy(link(), "Link")}
            />
          }
        >
          <LinkIcon aria-hidden="true" />
        </TooltipTrigger>
        <TooltipContent>Copy link</TooltipContent>
      </Tooltip>
      {onFullPage ? (
        <Tooltip>
          <TooltipTrigger
            render={
              <Button
                variant="ghost"
                size="icon-sm"
                aria-label="Open as a full page"
                aria-keyshortcuts="Shift+F"
                onClick={onFullPage}
              />
            }
          >
            <Maximize2Icon aria-hidden="true" />
          </TooltipTrigger>
          <TooltipContent>Full page (⇧F)</TooltipContent>
        </Tooltip>
      ) : null}
      {/* TASK-LOCK: an agent is working on it; managers may stop it to move the task. */}
      <StopAgentButton api={api} company={company} task={task} />
      <AlertDialog>
        <DropdownMenu>
          <DropdownMenuTrigger
            render={<Button variant="ghost" size="icon-sm" aria-label="More actions" />}
          >
            <EllipsisIcon aria-hidden="true" />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {/* WOS-BUNDLE */}
            <DropdownMenuItem onClick={() => setDuplicating(true)}>
              <CopyIcon aria-hidden="true" />
              Duplicate…
            </DropdownMenuItem>
            {/* B4-ITEM-TEMPLATES: new tasks on the board can start from this one */}
            <DropdownMenuItem onClick={() => setSavingTemplate(true)}>
              <BookmarkPlusIcon aria-hidden="true" />
              Save as template…
            </DropdownMenuItem>
            {/* Parity P5: this task as a routine (managers) */}
            {isManager(company) ? (
              <DropdownMenuItem onClick={() => setRepeating(true)}>
                <RepeatIcon aria-hidden="true" />
                Repeat…
              </DropdownMenuItem>
            ) : null}
            {canManage ? (
              <DropdownMenuItem onClick={onCustomize}>
                <LayoutTemplateIcon aria-hidden="true" />
                Customize layout…
              </DropdownMenuItem>
            ) : null}
            <DropdownMenuItem onClick={() => copy(task.id, "Task ID")}>
              <HashIcon aria-hidden="true" />
              Copy ID
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setKeys(true)}>
              <KeyboardIcon aria-hidden="true" />
              Keyboard shortcuts
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <AlertDialogTrigger render={<DropdownMenuItem variant="destructive" />}>
              <Trash2Icon aria-hidden="true" />
              Delete task
            </AlertDialogTrigger>
          </DropdownMenuContent>
        </DropdownMenu>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {taskRef(task)}?</AlertDialogTitle>
            <AlertDialogDescription>
              “{task.title}” goes for everyone; its subtasks stay, without a parent. Archive the
              board instead if you might come back to it.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep it</AlertDialogCancel>
            <AlertDialogAction variant="destructive" onClick={remove}>
              Delete task
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AlertDialog open={keys} onOpenChange={setKeys}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Task shortcuts</AlertDialogTitle>
          </AlertDialogHeader>
          <dl className="m-0 grid grid-cols-[6rem_1fr] gap-x-3 gap-y-1.5 text-sm">
            {KEYBOARD_MAP.map((k) => (
              <div key={k.keys} className="contents">
                <dt>
                  <kbd className="rounded-sm border border-border px-1.5 text-xs">{k.keys}</kbd>
                </dt>
                <dd className="m-0 text-muted-foreground">{k.label}</dd>
              </div>
            ))}
          </dl>
          <AlertDialogFooter>
            <AlertDialogCancel>Close</AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      {duplicating ? (
        <DuplicateTaskDialog
          api={api}
          company={company}
          task={task}
          open={duplicating}
          onOpenChange={setDuplicating}
          onOpenTask={onOpenTask}
        />
      ) : null}
      {repeating ? (
        <RepeatDialog api={api} company={company} task={task} onClose={() => setRepeating(false)} />
      ) : null}
      {savingTemplate ? (
        <SaveAsTemplateDialog
          api={api}
          company={company}
          task={task}
          onClose={() => setSavingTemplate(false)}
        />
      ) : null}
    </>
  );
}
